import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCustomers } from "../lib/sync";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  ChevronRight,
  Search,
  Building2,
  Trash2,
  Archive,
  ArchiveRestore,
  CheckSquare,
  Square,
  X,
} from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [delTarget, setDelTarget] = useState(null);
  const [bulkDelOpen, setBulkDelOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [hiddenIds, setHiddenIds] = useState(new Set());

  const load = () => getCustomers().then(setCustomers).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.name) return toast.error("Customer name is required");
    setSaving(true);
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setOpen(false);
      setForm({});
      load();
    } catch (e) {
      toast.error("Could not add customer (offline?)");
    } finally {
      setSaving(false);
    }
  };

  // Deferred delete with an Undo window. The API call only fires after the
  // toast times out; tapping Undo cancels it and restores the row(s).
  const deferDelete = (ids, label) => {
    setHiddenIds((prev) => new Set([...prev, ...ids]));
    const timer = setTimeout(async () => {
      let failed = 0;
      for (const id of ids) {
        try {
          await api.delete(`/customers/${id}`);
        } catch (e) {
          failed += 1;
        }
      }
      setHiddenIds((prev) => {
        const n = new Set(prev);
        ids.forEach((i) => n.delete(i));
        return n;
      });
      if (failed) toast.error(`Could not delete ${failed} customer${failed > 1 ? "s" : ""}`);
      load();
    }, 6000);
    toast(label, {
      description: "Removing permanently in a few seconds…",
      duration: 6000,
      action: {
        label: "Undo",
        onClick: () => {
          clearTimeout(timer);
          setHiddenIds((prev) => {
            const n = new Set(prev);
            ids.forEach((i) => n.delete(i));
            return n;
          });
          toast.success("Restored");
        },
      },
    });
  };

  const archiveCustomers = async (ids) => {
    try {
      await Promise.all(ids.map((id) => api.post(`/customers/${id}/archive`)));
      toast.success(ids.length > 1 ? `${ids.length} customers archived` : "Customer archived");
      exitSelect();
      load();
    } catch (e) {
      toast.error("Could not archive");
    }
  };

  const unarchiveCustomer = async (id) => {
    try {
      await api.post(`/customers/${id}/unarchive`);
      toast.success("Customer restored");
      load();
    } catch (e) {
      toast.error("Could not restore");
    }
  };

  const exitSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const filtered = customers.filter((c) => {
    if (hiddenIds.has(c.id)) return false;
    if (isAdmin && (showArchived ? !c.archived : c.archived)) return false;
    if (!isAdmin && c.archived) return false;
    return [c.name, c.contact_person, c.email, c.phone]
      .join(" ")
      .toLowerCase()
      .includes(q.toLowerCase());
  });

  const archivedCount = customers.filter((c) => c.archived && !hiddenIds.has(c.id)).length;

  return (
    <div className="space-y-5 pb-24">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">Customers</h1>
        <div className="flex items-center gap-2">
          {isAdmin &&
            (selectMode ? (
              <Button variant="outline" className="h-11" onClick={exitSelect} data-testid="cancel-select-btn">
                <X className="mr-1.5 h-4 w-4" /> Cancel
              </Button>
            ) : (
              <Button variant="outline" className="h-11" onClick={() => setSelectMode(true)} data-testid="select-mode-btn">
                <CheckSquare className="mr-1.5 h-4 w-4" /> Select
              </Button>
            ))}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-customer-btn" className="h-11">
                <Plus className="mr-1.5 h-4 w-4" /> New
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-auto">
              <DialogHeader>
                <DialogTitle>New customer</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {[
                  ["name", "Customer name *"],
                  ["contact_person", "Contact person"],
                  ["email", "Email"],
                  ["phone", "Phone"],
                  ["billing_address", "Billing address"],
                ].map(([k, label]) => (
                  <div key={k} className="space-y-1.5">
                    <Label>{label}</Label>
                    <Input
                      value={form[k] || ""}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      data-testid={`customer-${k}`}
                      className="h-11"
                    />
                  </div>
                ))}
                <div className="space-y-1.5">
                  <Label>Notes</Label>
                  <Textarea
                    value={form.notes || ""}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="customer-notes"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={save} disabled={saving} data-testid="save-customer-btn" className="h-11 w-full">
                  Save customer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search customers…"
          className="h-12 pl-10"
          data-testid="customer-search"
        />
      </div>

      {isAdmin && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowArchived(false)}
            data-testid="filter-active"
            className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
              !showArchived ? "border-primary bg-primary text-white" : "border-border bg-white text-slate-600"
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setShowArchived(true)}
            data-testid="filter-archived"
            className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
              showArchived ? "border-primary bg-primary text-white" : "border-border bg-white text-slate-600"
            }`}
          >
            Archived{archivedCount ? ` (${archivedCount})` : ""}
          </button>
        </div>
      )}

      <div className="space-y-2.5">
        {filtered.map((c) => {
          const isSel = selected.has(c.id);
          return (
            <Card
              key={c.id}
              onClick={() => (selectMode ? toggleSelect(c.id) : navigate(`/customers/${c.id}`))}
              className={`flex cursor-pointer items-center justify-between p-4 hover:border-primary/40 ${
                isSel ? "border-primary ring-1 ring-primary" : ""
              }`}
              data-testid={`customer-card-${c.id}`}
            >
              <div className="flex min-w-0 items-center gap-3">
                {selectMode && (
                  <span className="text-primary" data-testid={`select-box-${c.id}`}>
                    {isSel ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5 text-slate-300" />}
                  </span>
                )}
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Building2 className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-bold text-slate-900">{c.name}</span>
                    {c.archived && (
                      <Badge variant="secondary" className="bg-slate-100 text-[10px] text-slate-500">
                        Archived
                      </Badge>
                    )}
                  </div>
                  <div className="truncate text-xs text-slate-500">
                    {c.contact_person} {c.phone ? `• ${c.phone}` : ""}
                  </div>
                </div>
              </div>

              {!selectMode &&
                (isAdmin ? (
                  <div className="flex items-center gap-1">
                    {c.archived ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          unarchiveCustomer(c.id);
                        }}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-emerald-50 hover:text-primary"
                        data-testid={`unarchive-customer-${c.id}`}
                        title="Restore from archive"
                      >
                        <ArchiveRestore className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          archiveCustomers([c.id]);
                        }}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        data-testid={`archive-customer-${c.id}`}
                        title="Archive customer"
                      >
                        <Archive className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDelTarget(c);
                      }}
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                      data-testid={`delete-customer-${c.id}`}
                      title="Delete customer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </div>
                ) : (
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                ))}
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <p className="py-8 text-center text-sm text-slate-400">No customers found.</p>
        )}
      </div>

      {/* bulk action bar */}
      {selectMode && selected.size > 0 && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-white/95 p-3 backdrop-blur"
          data-testid="bulk-action-bar"
        >
          <div className="mx-auto flex max-w-2xl items-center gap-2">
            <span className="text-sm font-semibold text-slate-700" data-testid="bulk-selected-count">
              {selected.size} selected
            </span>
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                onClick={() => archiveCustomers([...selected])}
                data-testid="bulk-archive-btn"
              >
                <Archive className="mr-1.5 h-4 w-4" /> Archive
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => setBulkDelOpen(true)}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="mr-1.5 h-4 w-4" /> Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* single delete confirm */}
      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the customer along with its sites, heat pumps,
              service reports and scheduled jobs. You'll get a few seconds to undo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const c = delTarget;
                setDelTarget(null);
                deferDelete([c.id], `${c.name} deleted`);
              }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-customer"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* bulk delete confirm */}
      <AlertDialog open={bulkDelOpen} onOpenChange={setBulkDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} customers?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {selected.size} customers and all their sites,
              heat pumps, reports and jobs. You'll get a few seconds to undo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const ids = [...selected];
                setBulkDelOpen(false);
                exitSelect();
                deferDelete(ids, `${ids.length} customers deleted`);
              }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-bulk-delete"
            >
              Delete {selected.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
